// The city view's scene: the Hearthstone Refined city (City View handoff, site version 20, Oct 12 2026 —
// `Art/UI Redesign/UI Refresh/City View`). Pure data + maths, no DOM.
//
// One frame for everything — terrain, streets, grounds, walls, buildings, shadows, labels, taps, the camera: the
// handoff's export frame, 1181 x 1343 px. Its pixel (0, 0) is the approved native world's (0, −12) (12 px of headroom keep
// the Tier III Hall's northern finial whole), so a native-world point (x, y) is frame point (x, y + 12) — `toFrame`.
// The layers are the handoff's own pixels, cropped by art-src/export_hearthstone_city.py, which also wrote
// hearthstoneScene.json: where each crop goes, which building and tier owns it, the master's stack order, the label
// points and the tap outlines.
//
// The handoff's four full-city PNGs are coordinated reference snapshots, not a city state. Here every building wears the
// look of its own level (constants.tierOf bands); level 0 draws nothing (in construction mode its plot can still be
// tapped). The Academy and the Rally Point have one look whenever built; the Wheel always stands; the loyalty flag stands
// while the City Hall does. The walls, the gate, its threshold and its apron follow the Walls' level. The shared streets
// are drawn in the material of the City Hall's tier — a visual-only policy (dirt when the City Hall is level 0) that
// changes nothing else — and each building's own entrance spur in the material painted with its look.
import data from "./hearthstoneScene.json";
import { BUILDING_KEYS, tierOf, type BuildingKey } from "./constants";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Owner =
  | "cityhall" | "barracks" | "workshop" | "stables" | "iron" | "quarry" | "farm" | "lumber" | "warehouse" | "forge" | "academy"
  | "market" | "hiding" | "walls" | "rally" | "wheel" | "flag";

/** A cropped layer of the master, placed at (x, y) in the frame, w x h px. */
export interface SceneLayer {
  id: string;
  /** its place in the master's stack (SLOTS order, back to front) */
  slot: string;
  owner: string;
  /** the look (1–4) it belongs to; none for the fixtures */
  tier?: number;
  /** the street material (1 dirt · 2 gravel and partial paving · 3 rough stone · 4 paved stone) it is painted in */
  material?: number;
  part?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  file: string;
}

/** One look of a building: its painted extent, its tap outline(s) and its measured ground contact, frame px. */
export interface Look {
  bbox: [number, number, number, number];
  /** closed polygons, flattened x0, y0, x1, y1, … */
  hit: number[][];
  contact?: [number, number];
}

interface OwnerData {
  key?: string;
  /** where its plate hangs (the handoff's reference label point) */
  label?: [number, number];
  looks: Record<string, Look>;
  /** the Walls only: the gate's physical threshold */
  threshold?: [number, number];
}

interface SceneData {
  frame: { width: number; height: number; nativeOriginY: number };
  dir: string;
  terrain: Omit<SceneLayer, "id" | "slot" | "owner">;
  slots: string[];
  layers: SceneLayer[];
  owners: Record<string, OwnerData>;
  coordinated: Record<"I" | "II" | "III" | "IV", { levels: Record<string, number>; layers: string[]; image: string }>;
}

const SCENE_DATA = data as unknown as SceneData;
export const SCENE_LAYERS: readonly SceneLayer[] = SCENE_DATA.layers;
export const SLOTS: readonly string[] = SCENE_DATA.slots;
export const OWNERS = SCENE_DATA.owners as Record<Owner, OwnerData>;
/** The four coordinated reference cities (the handoff's reference-fixtures levels) and the master's stack for each. */
export const COORDINATED = SCENE_DATA.coordinated;

const W = SCENE_DATA.frame.width;
const H = SCENE_DATA.frame.height;
/** Native world y + this = frame y. */
export const HEADROOM = -SCENE_DATA.frame.nativeOriginY;
export const toFrame = (x: number, y: number): [number, number] => [x, y + HEADROOM];

export const SCENE = {
  W,
  H,
  terrain: SCENE_DATA.terrain as Rect & { file: string },
  /** how far the camera may pan side to side: the frame (the walls, towers included, lie inside it) */
  wallX0: 0,
  wallX1: W,
  /** the walls side to side (the old overview callouts' columns hugged these) */
  towersX0: 0,
  towersX1: W,
  /** the gate: the Walls' tap target and plate; its physical threshold (native [702, 1204]) */
  gate: { x: OWNERS.walls.label![0], y: OWNERS.walls.label![1], threshold: OWNERS.walls.threshold! },
  /** where "close-up" opens: the heart of the city, the Hall–Workshop–Stables–Quarry junction (the handoff's junction close-up) */
  close: { x: (280 + 915) / 2, y: (330 + 805) / 2 + 12 },
} as const;

/** Where the exporter put the files (kit/art.ts `art.city.hearthstone` builds the URLs; a unit test keeps the two equal). */
export const SCENE_DIR = SCENE_DATA.dir;

/** The scene owner of each game building (the Walls are the gate). */
export const OWNER_OF: Record<BuildingKey, Owner> = {
  b_hq: "cityhall", b_r: "rally", b_b: "barracks", b_st: "stables", b_wo: "workshop", b_w: "lumber", b_c: "quarry", b_i: "iron",
  b_wh: "warehouse", b_f: "farm", b_s: "forge", b_a: "academy", b_m: "market", b_wa: "walls", b_h: "hiding",
};
export const KEY_OF_OWNER = Object.fromEntries(Object.entries(OWNER_OF).map(([k, o]) => [o, k])) as Partial<Record<Owner, BuildingKey>>;

export type Levels = Partial<Record<BuildingKey, number>>;

/** Which look each owner wears for these levels: 0 nothing, else the look's key in OWNERS[owner].looks. */
export function looksOf(levels: Levels): Record<Owner, number> {
  const lv = (k: BuildingKey) => Math.max(0, Number(levels[k]) || 0);
  const out = {} as Record<Owner, number>;
  for (const k of BUILDING_KEYS) out[OWNER_OF[k]] = tierOf(k, lv(k));
  out.academy = lv("b_a") > 0 ? 4 : 0; // its one look is the master's (tier IV) Academy
  out.rally = lv("b_r") > 0 ? 1 : 0;
  out.wheel = 1;
  out.flag = lv("b_hq") > 0 ? 1 : 0;
  return out;
}

/** The shared streets' material: the City Hall's tier, dirt (1) with no City Hall. Visual only. */
export const streetMaterial = (levels: Levels): 1 | 2 | 3 | 4 => (tierOf("b_hq", Math.max(0, Number(levels.b_hq) || 0)) || 1) as 1 | 2 | 3 | 4;

const Z = new Map(SLOTS.map((s, i) => [s, i]));
export const zOf = (slot: string) => Z.get(slot) ?? -1;

/** The layers a city with these levels draws, back to front (over the shared terrain). */
export function layersFor(levels: Levels): SceneLayer[] {
  const look = looksOf(levels);
  const material = streetMaterial(levels);
  const out = SCENE_LAYERS.filter((l) => {
    const s = l.slot;
    if (s === "street:route") return l.material === material;
    if (s === "street:spur:rally") return look.rally > 0 && l.material === material;
    if (s === "street:apron" || s.startsWith("walls:")) return look.walls === l.tier;
    if (s === "fixture:wheel") return true;
    if (s === "fixture:rally") return look.rally > 0;
    if (s === "fixture:flag") return look.flag > 0;
    // buildings, their entrance spurs, grounds, floor repairs and footing shadows follow their own building's look
    return look[l.owner as Owner] === l.tier;
  });
  return out.sort((a, b) => zOf(a.slot) - zOf(b.slot));
}

// ------------------------------------------------------------------ taps

/** What a tap can land on: a standing building (its painted outline), the Wheel, the loyalty flag, the gate (the Walls),
 *  or — in construction mode — an empty plot (the outline its first look will have). */
export interface HitTarget {
  owner: Owner;
  /** what it opens: a building key, the Wheel or Loyalty */
  key: BuildingKey | "wheel" | "loyalty";
  /** an empty plot (construction mode only) */
  site: boolean;
  look: Look;
}

/** The look an owner's plot uses when it is empty: the outline its first look will fill. */
export const siteLook = (owner: Owner): Look => {
  const looks = OWNERS[owner].looks;
  return looks["1"] ?? looks[Object.keys(looks)[0]];
};

/** The owner's look for these levels, or its empty plot's. */
export function lookFor(owner: Owner, levels: Levels): { look: Look; site: boolean } {
  const n = looksOf(levels)[owner];
  return n > 0 ? { look: OWNERS[owner].looks[String(n)], site: false } : { look: siteLook(owner), site: true };
}

/** The front-most slot an owner's tap outline stands at (its top layer). */
function topSlot(owner: Owner): number {
  if (owner === "walls") return zOf("walls:gate");
  if (owner === "wheel" || owner === "rally" || owner === "flag") return zOf(`fixture:${owner}`);
  return Math.max(zOf(`building:${owner}:architecture`), zOf(`building:${owner}:yard`), zOf(`building:${owner}:whole`));
}

/** Tap targets, front-most first (the order the art is stacked), then the empty plots (construction mode only). */
export function hitTargets(levels: Levels, construction: boolean): HitTarget[] {
  const look = looksOf(levels);
  const standing: HitTarget[] = [];
  const sites: HitTarget[] = [];
  for (const owner of Object.keys(OWNERS) as Owner[]) {
    const key: HitTarget["key"] = owner === "wheel" ? "wheel" : owner === "flag" ? "loyalty" : KEY_OF_OWNER[owner]!;
    if (look[owner] > 0) standing.push({ owner, key, site: false, look: OWNERS[owner].looks[String(look[owner])] });
    else if (construction && owner !== "flag" && owner !== "wheel") sites.push({ owner, key, site: true, look: siteLook(owner) });
  }
  standing.sort((a, b) => topSlot(b.owner) - topSlot(a.owner));
  return [...standing, ...sites];
}

/** Even-odd point-in-polygon. */
export function inPoly(poly: number[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const xi = poly[i], yi = poly[i + 1], xj = poly[j], yj = poly[j + 1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Distance from a point to a polygon's outline. */
export function distToPoly(poly: number[], x: number, y: number): number {
  let best = Infinity;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const ax = poly[j], ay = poly[j + 1], bx = poly[i], by = poly[i + 1];
    const dx = bx - ax, dy = by - ay;
    const t = dx || dy ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy))) : 0;
    best = Math.min(best, Math.hypot(x - (ax + t * dx), y - (ay + t * dy)));
  }
  return best;
}

/** What a tap at frame point (x, y) lands on: the front-most outline that holds it; failing that, the nearest outline
 *  within `tol` frame px (a finger's slack); else nothing (open ground). */
export function hitAt(targets: HitTarget[], x: number, y: number, tol = 0): HitTarget | null {
  for (const t of targets) {
    const [x0, y0, x1, y1] = t.look.bbox;
    if (x < x0 - 4 || x > x1 + 4 || y < y0 - 4 || y > y1 + 4) continue;
    if (t.look.hit.some((p) => inPoly(p, x, y))) return t;
  }
  if (tol <= 0) return null;
  let best: HitTarget | null = null;
  let bd = tol;
  for (const t of targets) {
    const [x0, y0, x1, y1] = t.look.bbox;
    if (x < x0 - tol - 4 || x > x1 + tol + 4 || y < y0 - tol - 4 || y > y1 + tol + 4) continue;
    for (const p of t.look.hit) {
      const d = distToPoly(p, x, y);
      if (d < bd) {
        bd = d;
        best = t;
      }
    }
  }
  return best;
}

/** Where the construction pop-up points for an owner, frame px: its look's middle, a little below its top (`above`),
 *  and under its plate (`below`, used when the pop-up can't open above — the City Hall at the top of the city). */
export function popAnchor(owner: Owner, levels: Levels): { x: number; above: number; below: number } {
  const { look } = lookFor(owner, levels);
  const [x0, y0, x1, y1] = look.bbox;
  const label = OWNERS[owner].label ?? [(x0 + x1) / 2, y1];
  return { x: (x0 + x1) / 2, above: y0 + (y1 - y0) * 0.2, below: Math.max(label[1] + 92, y1 + 12) };
}
